import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './TermsConditionPage.css';

const LAST_UPDATED = { EN: 'August 19, 2026', AR: '19 أغسطس 2026' };

const CARRIERS = {
  EN: [
    { id: 'stc', name: 'STC', trial: '1-day free trial', price: '0.200 KWD / day', unsub: 'Send "Stop 1" to 50712' },
    { id: 'ooredoo', name: 'Ooredoo', trial: 'No free trial', price: '0.700 KWD / week', unsub: 'Send "Stop1" to 1769' },
    { id: 'zain', name: 'Zain', trial: '1-day free trial', price: '0.700 KWD / week', unsub: 'Send "Unsub KS" to 97979' },
  ],
  AR: [
    { id: 'stc', name: 'STC', trial: 'تجربة مجانية ليوم واحد', price: '0.200 د.ك / يوم', unsub: 'أرسل "Stop 1" إلى 50712' },
    { id: 'ooredoo', name: 'Ooredoo', trial: 'بدون تجربة مجانية', price: '0.700 د.ك / أسبوعياً', unsub: 'أرسل "Stop1" إلى 1769' },
    { id: 'zain', name: 'Zain', trial: 'تجربة مجانية ليوم واحد', price: '0.700 د.ك / أسبوعياً', unsub: 'أرسل "Unsub KS" إلى 97979' },
  ],
};

const SECTION_ORDER = [
  'welcome', 'eligibility', 'pricing', 'trial', 'billing', 'cancel',
  'refund', 'liability', 'availability', 'compatibility', 'data', 'privacy', 'changes', 'law', 'contact',
];

const CONTENT = {
  EN: {
    heroTitle: 'Terms & Conditions',
    heroSubtitle: 'Please read these terms carefully before subscribing to Kid Security.',
    tocLabel: 'On this page',
    closing: 'By continuing with your subscription, you confirm that you have read, understood, and agreed to these terms and conditions.',
    footerNote: 'Kid Security is a parental control service operated by Strong Bits Computer Systems. These terms apply to subscribers of the Kid Security value-added service billed through participating telecom providers in Kuwait.',
    backToTop: 'Back to top',
    sections: {
      welcome: {
        title: 'Welcome',
        body: [
          'Welcome to Kid Security, the comprehensive parental control app designed to give you total peace of mind by keeping your children safe online through real-time location tracking, screen time management, and intelligent content filtering.',
          'Depending on your network provider, Ooredoo users can subscribe for 0.700 KWD per week, STC users get a 1-day free trial followed by 0.200 KWD per day, and Zain users receive a 1-day free trial followed by 0.700 KWD per week.',
          'Your subscription renews automatically so your protection is never interrupted, but you remain in complete control and can unsubscribe at any time by sending Stop 1 to 50712 for STC, Stop1 to 1769 for Ooredoo, or Unsub KS to 97979 for Zain.',
        ],
      },
      eligibility: {
        title: 'Eligibility',
        body: [
          'You must be 18 years of age or older to subscribe to Kid Security. By subscribing, you confirm that you meet this age requirement and that you are the authorized user of the mobile line used to subscribe, or that you have the account owner\'s consent to do so.',
        ],
      },
      pricing: {
        title: 'Subscription Plans & Pricing',
        intro: 'Pricing depends on your network provider, as shown below:',
      },
      trial: {
        title: 'Free Trial Period',
        body: [
          'STC and Zain subscribers receive a 1-day free trial. If you do not cancel before the trial ends, billing will begin automatically at the rate listed above. Ooredoo does not offer a free trial, and billing begins immediately upon subscription.',
        ],
      },
      billing: {
        title: 'Automatic Renewal & Billing',
        body: [
          'Your subscription renews automatically (daily or weekly, depending on your network provider) until you cancel it. Charges are deducted from your mobile credit (prepaid) or added to your monthly bill (postpaid).',
          'All charges are inclusive of applicable taxes and will appear on your telecom provider\'s bill under "Kid Security."',
        ],
      },
      cancel: {
        title: 'How to Cancel Your Subscription',
        intro: 'You may unsubscribe at any time by sending the code for your network provider:',
        note: 'Once cancelled, the service will stop and no further charges will be applied. Cancellation may take effect from the next billing cycle.',
      },
      refund: {
        title: 'Refund Policy',
        body: [
          'No refunds will be issued for subscription charges already paid, and no partial-period amounts will be prorated, except where required by applicable law.',
        ],
      },
      liability: {
        title: 'Limitation of Liability',
        body: [
          'Kid Security is provided "as is." To the maximum extent permitted by applicable law, Strong Bits Computer Systems shall not be liable for any indirect, incidental, or consequential damages, or for any service interruption or delay caused by your telecom provider or any third party beyond our reasonable control.',
        ],
      },
      availability: {
        title: 'Service Availability',
        body: [
          'The Kid Security service depends on the availability of your telecom provider\'s network and is subject to network coverage. Strong Bits Computer Systems is not liable for any disruption or downtime caused by your telecom provider.',
        ],
      },
      compatibility: {
        title: 'Device Compatibility',
        body: [
          'Some Kid Security features may not work as expected if the child\'s device is an iOS device (iPhone/iPad), due to Apple\'s privacy restrictions, which limit the background location, screen time, and content-filtering capabilities available to third-party apps.',
          'For the optimal experience, we recommend that the child\'s device runs Android.',
        ],
      },
      data: {
        title: 'Data Usage',
        body: [
          'Using the Kid Security app may incur additional data charges from your telecom provider, depending on your data plan.',
        ],
      },
      privacy: {
        title: 'Privacy',
        body: [
          'Your privacy matters to us. Any data collected as part of the Kid Security service is handled in accordance with our Privacy Policy.',
        ],
      },
      changes: {
        title: 'Changes to These Terms',
        body: [
          'Strong Bits Computer Systems reserves the right to modify these terms and conditions at any time. Subscribers will be notified of any material changes, and continued use of the service after such changes constitutes acceptance of the updated terms.',
        ],
      },
      law: {
        title: 'Governing Law',
        body: [
          'These terms are governed by and construed in accordance with the laws of the State of Kuwait in relation to the provision of the service, without prejudice to the registration of Strong Bits Computer Systems in Dubai, United Arab Emirates.',
        ],
      },
      contact: {
        title: 'Contact Us',
        body: [
          'Strong Bits Computer Systems, Dubai, United Arab Emirates.',
          'For any questions about these terms or the Kid Security service, please reach out through the support section of the Kid Security app.',
        ],
      },
    },
  },
  AR: {
    heroTitle: 'الشروط والأحكام',
    heroSubtitle: 'يرجى قراءة هذه الشروط بعناية قبل الاشتراك في خدمة Kid Security.',
    tocLabel: 'في هذه الصفحة',
    closing: 'من خلال متابعة اشتراكك، فإنك تؤكد أنك قد قرأت هذه الشروط والأحكام وفهمتها ووافقت عليها.',
    footerNote: 'خدمة Kid Security هي خدمة رقابة أبوية تُقدَّم من قبل شركة Strong Bits Computer Systems. تنطبق هذه الشروط على مشتركي خدمة Kid Security ذات القيمة المضافة التي يتم فوترتها عبر مزودي الاتصالات المشاركين في الكويت.',
    backToTop: 'العودة للأعلى',
    sections: {
      welcome: {
        title: 'مرحبًا بك',
        body: [
          'مرحبًا بك في Kid Security، تطبيق الرقابة الأبوية الشامل المصمم لمنحك راحة بال تامة من خلال الحفاظ على سلامة أطفالك على الإنترنت عبر تتبع الموقع في الوقت الفعلي، وإدارة وقت الشاشة، والفلترة الذكية للمحتوى.',
          'حسب مزود الشبكة الخاص بك، يمكن لمشتركي Ooredoo الاشتراك مقابل 0.700 دينار كويتي أسبوعيًا، ويحصل مشتركو STC على تجربة مجانية ليوم واحد يتبعها 0.200 دينار كويتي يوميًا، بينما يحصل مشتركو Zain على تجربة مجانية ليوم واحد تتبعها 0.700 دينار كويتي أسبوعيًا.',
          'يتجدد اشتراكك تلقائيًا حتى تبقى حمايتك دون انقطاع، لكنك تحتفظ بالتحكم الكامل ويمكنك إلغاء الاشتراك في أي وقت عبر إرسال Stop 1 إلى 50712 لمستخدمي STC، أو Stop1 إلى 1769 لمستخدمي Ooredoo، أو Unsub KS إلى 97979 لمستخدمي Zain.',
        ],
      },
      eligibility: {
        title: 'متطلبات الأهلية',
        body: [
          'يجب أن يكون عمرك 18 عامًا فأكثر للاشتراك في خدمة Kid Security. من خلال الاشتراك، فإنك تقر بأنك تستوفي هذا الشرط العمري وأنك المخوَّل باستخدام رقم الهاتف المحمول المستخدم في الاشتراك، أو أنك حصلت على موافقة مالك الخط للقيام بذلك.',
        ],
      },
      pricing: {
        title: 'خطط الاشتراك والأسعار',
        intro: 'تختلف الأسعار حسب مزود الشبكة الخاص بك على النحو التالي:',
      },
      trial: {
        title: 'فترة التجربة المجانية',
        body: [
          'يحصل مشتركو STC و Zain على تجربة مجانية مدتها يوم واحد. في حال عدم إلغاء الاشتراك قبل انتهاء فترة التجربة، سيبدأ إصدار الفواتير تلقائيًا وفقًا للسعر الموضح أعلاه. لا تتوفر فترة تجربة مجانية لمستخدمي Ooredoo، ويبدأ إصدار الفواتير فور الاشتراك.',
        ],
      },
      billing: {
        title: 'التجديد التلقائي والفوترة',
        body: [
          'يتجدد اشتراكك تلقائيًا (يوميًا أو أسبوعيًا بحسب مزود الشبكة) ما لم تقم بإلغائه. سيتم خصم الرسوم من رصيد هاتفك المحمول (الدفع المسبق) أو إضافتها إلى فاتورتك الشهرية (الدفع الآجل).',
          'جميع الرسوم شاملة للضرائب المعمول بها وستظهر على فاتورة مزود خدمتك تحت اسم "Kid Security".',
        ],
      },
      cancel: {
        title: 'كيفية إلغاء الاشتراك',
        intro: 'يمكنك إلغاء اشتراكك في أي وقت عبر إرسال الرمز الخاص بمزود شبكتك:',
        note: 'بعد الإلغاء، سيتوقف الاشتراك ولن يتم خصم أي رسوم إضافية. قد يبدأ سريان الإلغاء اعتبارًا من دورة الفوترة التالية.',
      },
      refund: {
        title: 'سياسة الاسترداد',
        body: [
          'لا يتم إصدار أي استرداد للرسوم المدفوعة بالفعل مقابل الاشتراك، ولا يتم احتساب أي مبالغ بشكل تناسبي عن فترة غير مكتملة، إلا إذا كان القانون المعمول به يقتضي خلاف ذلك.',
        ],
      },
      liability: {
        title: 'حدود المسؤولية',
        body: [
          'يتم تقديم خدمة Kid Security "كما هي". وإلى أقصى حد يسمح به القانون المعمول به، لا تتحمل شركة Strong Bits Computer Systems أي مسؤولية عن أي أضرار غير مباشرة أو عرضية أو تبعية، أو عن أي انقطاع أو تأخير في الخدمة ناتج عن مزود الاتصالات الخاص بك أو أي طرف ثالث خارج عن سيطرتنا المعقولة.',
        ],
      },
      availability: {
        title: 'توفر الخدمة',
        body: [
          'تعتمد خدمة Kid Security على مدى توفر شبكة مزود الاتصالات الخاص بك، وتخضع لتغطية الشبكة. لا تتحمل شركة Strong Bits Computer Systems أي مسؤولية عن أي انقطاع أو توقف ناتج عن مزود الاتصالات الخاص بك.',
        ],
      },
      compatibility: {
        title: 'توافق الأجهزة',
        body: [
          'قد لا تعمل بعض ميزات Kid Security كما هو متوقع إذا كان جهاز الطفل يعمل بنظام iOS (آيفون/آيباد)، وذلك بسبب قيود الخصوصية التي فرضتها Apple والتي تحد من إمكانيات تتبع الموقع في الخلفية، وإدارة وقت الشاشة، والفلترة الذكية للمحتوى المتاحة للتطبيقات التابعة لجهات خارجية.',
          'للحصول على أفضل تجربة، نوصي بأن يكون جهاز الطفل يعمل بنظام Android.',
        ],
      },
      data: {
        title: 'استخدام البيانات',
        body: [
          'قد يؤدي استخدام تطبيق Kid Security إلى تحمل رسوم بيانات إضافية من مزود خدمتك وفقًا لباقتك.',
        ],
      },
      privacy: {
        title: 'الخصوصية',
        body: [
          'خصوصيتك تهمنا. تتم معالجة أي بيانات يتم جمعها كجزء من خدمة Kid Security وفقًا لسياسة الخصوصية الخاصة بنا.',
        ],
      },
      changes: {
        title: 'التعديلات على هذه الشروط',
        body: [
          'تحتفظ شركة Strong Bits Computer Systems بالحق في تعديل هذه الشروط والأحكام في أي وقت. سيتم إخطار المشتركين بأي تغييرات جوهرية، ويُعد استمرارك في استخدام الخدمة بعد نشر هذه التعديلات موافقة منك عليها.',
        ],
      },
      law: {
        title: 'القانون الحاكم',
        body: [
          'تخضع هذه الشروط والأحكام وتُفسَّر وفقًا لقوانين دولة الكويت فيما يتعلق بتقديم الخدمة، دون الإخلال بتسجيل شركة Strong Bits Computer Systems في إمارة دبي، الإمارات العربية المتحدة.',
        ],
      },
      contact: {
        title: 'تواصل معنا',
        body: [
          'شركة Strong Bits Computer Systems، دبي، الإمارات العربية المتحدة.',
          'لأي استفسارات بخصوص هذه الشروط أو خدمة Kid Security، يُرجى التواصل معنا عبر قسم الدعم داخل تطبيق Kid Security.',
        ],
      },
    },
  },
};

function ShieldIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="26" height="26" {...props}>
      <path d="M12 3l7 3v5.2c0 4.6-3 8.7-7 9.8-4-1.1-7-5.2-7-9.8V6l7-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function TermsConditionPage() {
  const location = useLocation();
  const [language, setLanguage] = useState('EN');
  const contentRef = useRef(null);
  const [activeSection, setActiveSection] = useState(SECTION_ORDER[0]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const lang = searchParams.get('lang');
    if (!lang || (lang.toUpperCase() !== 'EN' && lang.toUpperCase() !== 'AR')) {
      return;
    }
    setLanguage(lang.toUpperCase());
  }, [location.search]);

  useEffect(() => {
    document.title = language === 'AR' ? 'الشروط والأحكام | Kid Security' : 'Terms & Conditions | Kid Security';
  }, [language]);

  const dir = language === 'AR' ? 'rtl' : 'ltr';
  const t = useMemo(() => CONTENT[language], [language]);
  const carriers = useMemo(() => CARRIERS[language], [language]);

  const scrollToSection = (id) => {
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="terms-page" dir={dir}>
      <header className="terms-header">
        <div className="brand-mark">
          <img alt="Kid Security" src={`${process.env.PUBLIC_URL}/kid-security-logo.png`} />
        </div>
        <div className="lang-toggle">
          <button
            type="button"
            className={language === 'EN' ? 'active' : ''}
            onClick={() => setLanguage('EN')}
          >
            English
          </button>
          <button
            type="button"
            className={language === 'AR' ? 'active' : ''}
            onClick={() => setLanguage('AR')}
          >
            عربي
          </button>
          <span className={`lang-pill ${language === 'AR' ? 'pos-1' : ''}`} />
        </div>
      </header>

      <section className="terms-hero">
        <div className="hero-badge">
          <ShieldIcon />
        </div>
        <h1>{t.heroTitle}</h1>
        <p className="subtitle">{t.heroSubtitle}</p>
        <div className="updated-pill">
          {language === 'AR' ? 'آخر تحديث:' : 'Last updated:'} <strong>{LAST_UPDATED[language]}</strong>
        </div>
      </section>

      <div className="terms-layout">
        <nav className="terms-toc" aria-label={t.tocLabel}>
          <span className="toc-label">{t.tocLabel}</span>
          {SECTION_ORDER.map((id) => (
            <button
              key={id}
              type="button"
              className={activeSection === id ? 'active' : ''}
              onClick={() => scrollToSection(id)}
            >
              {t.sections[id].title}
            </button>
          ))}
        </nav>

        <main className="terms-content" ref={contentRef}>
          {SECTION_ORDER.map((id, index) => {
            const section = t.sections[id];
            return (
              <article key={id} id={id} className="terms-section">
                <div className="section-head">
                  <span className="section-badge">{String(index + 1).padStart(2, '0')}</span>
                  <h2>{section.title}</h2>
                </div>

                {id === 'pricing' && (
                  <>
                    <p>{section.intro}</p>
                    <div className="carrier-grid">
                      {carriers.map((c) => (
                        <div className="carrier-card" key={c.id}>
                          <span className="carrier-trial">{c.trial}</span>
                          <span className="carrier-name">{c.name}</span>
                          <span className="carrier-price">{c.price}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {id === 'cancel' && (
                  <>
                    <p>{section.intro}</p>
                    <div className="carrier-grid">
                      {carriers.map((c) => (
                        <div className="carrier-card" key={c.id}>
                          <span className="carrier-name">{c.name}</span>
                          <span className="carrier-unsub"><strong>{c.unsub}</strong></span>
                        </div>
                      ))}
                    </div>
                    <p>{section.note}</p>
                  </>
                )}

                {id !== 'pricing' && id !== 'cancel' && section.body.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </article>
            );
          })}

          <p style={{ color: 'var(--ink-soft)', fontSize: '0.85rem', textAlign: 'center', margin: '0.5rem 0 0' }}>
            {t.closing}
          </p>
        </main>
      </div>

      <footer className="terms-footer">
        <div className="footer-shield">
          <ShieldIcon width="22" height="22" />
        </div>
        <div className="company-name">Strong Bits Computer Systems</div>
        <div className="company-address">Dubai, United Arab Emirates</div>
        <p className="footer-note">{t.footerNote}</p>
        <button type="button" className="back-to-top" onClick={scrollToTop}>
          {t.backToTop}
        </button>
      </footer>
    </div>
  );
}

export default TermsConditionPage;
